import {DOCUMENT} from "@angular/common";
import {Inject, Injectable, PLATFORM_ID} from "@angular/core";
import {Meta, Title} from "@angular/platform-browser";
import {PageUrlService} from "./pageUrl.service";


type SeoConfig = {
  title: string;
  description: string;
  canonicalPath: string; // "/blogs/slug" etc.
  og?: {
    type?: "website" | "article";
    imageUrl?: string; // absolute preferred
  };
  robots?: string;
};

@Injectable({providedIn: "root"})
export class SeoService {
  constructor(
    private meta: Meta,
    private title: Title,
    private pageUrlService: PageUrlService,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
  }

  apply(cfg: SeoConfig): void {
    const baseUrl = (this.pageUrlService.getBaseUrl() || "").replace(/\/$/, "");
    const canonicalUrl = baseUrl ? `${baseUrl}${cfg.canonicalPath}` : cfg.canonicalPath;

    this.cleanup();

    this.title.setTitle(cfg.title);

    this.meta.updateTag({name: "description", content: cfg.description});
    this.meta.updateTag({name: "robots", content: cfg.robots || "index, follow"});

    const ogType = cfg.og?.type || "website";
    const ogImage = this.normalizeUrl(cfg.og?.imageUrl || "", baseUrl);

    this.meta.updateTag({property: "og:type", content: ogType} as any);
    this.meta.updateTag({property: "og:site_name", content: "Schoonmaakwoede"} as any);
    this.meta.updateTag({property: "og:title", content: cfg.title} as any);
    this.meta.updateTag({property: "og:description", content: cfg.description} as any);
    this.meta.updateTag({property: "og:url", content: canonicalUrl} as any);
    if (ogImage) this.meta.updateTag({property: "og:image", content: ogImage} as any);

    this.setCanonical(canonicalUrl);
  }

  private cleanup(): void {
    // remove wrong canonical meta
    this.meta.removeTag(`name='canonical'`);

    // remove description duplicates
    this.meta.removeTag(`name='description'`);

    // remove OG tags written as property or incorrectly as name
    const ogKeys = ["og:type", "og:site_name", "og:title", "og:description", "og:url", "og:image"];
    for (const k of ogKeys) {
      this.meta.removeTag(`property='${k}'`);
      this.meta.removeTag(`name='${k}'`);
    }

    // remove existing canonical links
    const head = this.doc.head;
    if (head) {
      const links = Array.from(head.querySelectorAll(`link[rel="canonical"]`));
      for (const l of links) l.remove();
    }
  }

  private setCanonical(url: string): void {
    const head = this.doc.head;
    if (!head) return;

    const link = this.doc.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", url);
    head.appendChild(link);
  }

  private normalizeUrl(value: string, baseUrl: string): string {
    const v = (value || "").trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    if (!baseUrl) return v;
    if (v.startsWith("/")) return `${baseUrl}${v}`;
    return `${baseUrl}/${v}`;
  }
}
